/**
 * Rechte Spalte ARKO: derselbe Chat, in die Akte gelegt.
 * Kein zweites Fenster, kein View-Wechsel, kein Auto-Senden.
 */

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type AkteArkoHostApi = {
  host: HTMLElement | null;
  setHost: (el: HTMLElement | null) => void;
};

const AkteArkoHostContext = React.createContext<AkteArkoHostApi | null>(null);

export function AkteArkoHostProvider({ children }: { children: React.ReactNode }) {
  const [host, setHostState] = useState<HTMLElement | null>(null);
  const setHost = useCallback((el: HTMLElement | null) => {
    setHostState(el);
  }, []);
  const value = useMemo(() => ({ host, setHost }), [host, setHost]);
  return <AkteArkoHostContext.Provider value={value}>{children}</AkteArkoHostContext.Provider>;
}

export function useAkteArkoHost(): AkteArkoHostApi | null {
  return React.useContext(AkteArkoHostContext);
}

/** Ziel in der rechten Spalte. Der Chat wird hier hineingelegt, nicht neu erfunden. */
export function PortalAkteArkoHost() {
  const pane = useAkteArkoHost();
  const setHost = pane?.setHost;
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!setHost) return;
    setHost(ref.current);
    return () => setHost(null);
  }, [setHost]);
  return (
    <div
      ref={ref}
      className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden"
    />
  );
}

/** Voller ARKO-Reiter oder, bei geöffneter Akte, die rechte Spalte. Nie beides. */
export function AkteArkoChatMount({
  view,
  children,
}: {
  view: string;
  children: React.ReactNode;
}) {
  const host = useAkteArkoHost()?.host ?? null;
  if (view === 'chat') {
    return <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col">{children}</div>;
  }
  if (view === 'akten' && host) {
    return createPortal(
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">{children}</div>,
      host,
    );
  }
  return null;
}
